import { isElectron, isWindows } from "react-device-detect";
import { guaranteedGetUserMediaException, NoTrackError, setContentHint, } from "./utils";
import { Logger } from "../../Logger";
import { doIt } from "gather-common/dist/src/public/fpHelpers";
import { assertUnreachable } from "gather-common/dist/src/public/utils";
import { Err, Ok } from "gather-common-including-video/dist/src/public/result";
import ElectronInterop from "../../ElectronInterop";
import { metricsManager } from "../../Globals";
const SOUND_CONSTRAINTS = {
    echoCancellation: isWindows,
    autoGainControl: false,
    noiseSuppression: false,
};
const MAX_WIDTH = 2880;
const MAX_HEIGHT = 2880;
const LOW_FPS = 5;
const HIGH_FPS = 24;
const HIGH_RES = {
    width: { max: MAX_WIDTH },
    height: { max: MAX_HEIGHT },
};
const MED_RES = {
    width: { max: 1920 },
    height: { max: 1920 },
};
const LOW_RES = {
    width: { max: 1280 },
    height: { max: 1280 },
};
export var ScreenshareResolution;
(function (ScreenshareResolution) {
    ScreenshareResolution[ScreenshareResolution["LOW"] = 0] = "LOW";
    ScreenshareResolution[ScreenshareResolution["MED"] = 1] = "MED";
    ScreenshareResolution[ScreenshareResolution["HIGH"] = 2] = "HIGH";
})(ScreenshareResolution || (ScreenshareResolution = {}));
const SCREEN_CONSTRAINTS = {
    width: { max: MAX_WIDTH },
    height: { max: MAX_HEIGHT },
    cursor: "always",
};
const screenshareConstraints = (highFrameRate, screenshareResolution, options) => {
    const minFps = doIt(() => {
        switch (options.experiment) {
            case "Fps10":
                return 10;
            case "Fps15":
                return 15;
            case "Control":
            case undefined:
                return LOW_FPS;
            default:
                assertUnreachable(options.experiment);
        }
    });
    const frameRate = {
        frameRate: { max: highFrameRate ? HIGH_FPS : minFps },
        advanced: [
            { frameRate: { max: highFrameRate ? HIGH_FPS : minFps } },
            { frameRate: { min: highFrameRate ? HIGH_FPS : minFps } },
        ],
    };
    let resolution;
    switch (screenshareResolution) {
        case ScreenshareResolution.LOW:
            resolution = LOW_RES;
            break;
        case ScreenshareResolution.MED:
            resolution = MED_RES;
            break;
        case ScreenshareResolution.HIGH:
            resolution = HIGH_RES;
            break;
        default:
            throw Error(`No resolution for ${screenshareResolution}`);
    }
    const calculatedConstraints = {
        ...SCREEN_CONSTRAINTS,
        ...frameRate,
        ...resolution,
    };
    Logger.info(`[LMM] screenshare constraints for user in treatment ${options.experiment}`, calculatedConstraints);
    return calculatedConstraints;
};
const getScreenshareConstraints = ({ sound, highQuality, useNativePicker, }) => {
    if (isElectron && useNativePicker) {
        const video = {
            ...SCREEN_CONSTRAINTS,
            frameRate: { max: highQuality ? HIGH_FPS : LOW_FPS },
        };
        return { video, audio: sound };
    }
    else if (isElectron) {
        return { video: SCREEN_CONSTRAINTS, audio: sound };
    }
    else {
        return {
            video: SCREEN_CONSTRAINTS,
            audio: sound ? SOUND_CONSTRAINTS : false,
        };
    }
};
const getScreenshareStreamOrThrow = async (settings) => {
    const constraints = getScreenshareConstraints(settings);
    Logger.info("[LMM] calling getDisplayMedia with ", JSON.stringify(constraints));
    if (settings.useNativePicker) {
        constraints.audio = false;
    }
    let stream;
    if (settings.useNativePicker) {
        await ElectronInterop?.toggleActiveWindows?.(false);
    }
    try {
        stream = await navigator.mediaDevices.getDisplayMedia(constraints);
    }
    catch (e) {
        throw e;
    }
    finally {
        if (settings.useNativePicker) {
            await ElectronInterop?.toggleActiveWindows?.(true);
        }
    }
    if (!settings.useNativePicker) {
        await changeScreenshareTrackQuality(stream.getVideoTracks()[0], settings.highQuality, settings.resolution, { experiment: settings.experiment });
    }
    let resultStream = stream;
    if (settings.useNativePicker && settings.sound) {
        Logger.info("[LMM] Setting up loopback audio for native picker");
        const loopback = await navigator.mediaDevices.getDisplayMedia({ audio: true });
        for (const videoTrack of loopback.getVideoTracks()) {
            videoTrack.stop();
        }
        const audioTracks = loopback.getTracks().filter((x) => x.kind === "audio");
        if (audioTracks.length > 0) {
            resultStream = new MediaStream([...stream.getTracks(), ...audioTracks]);
        }
    }
    if (settings.sound) {
        setContentHint(resultStream.getAudioTracks()[0], "music");
    }
    return resultStream;
};
async function recordScreenshareResult({ stream, errors, useNativePicker, }) {
    const videoTrackSettings = stream?.getVideoTracks()[0]?.getSettings();
    const videoWidth = videoTrackSettings?.width;
    const videoHeight = videoTrackSettings?.height;
    const videoFrameRate = videoTrackSettings?.frameRate;
    const screenshareInfo = {
        result: stream ? "success" : "failure",
        videoWidth: videoWidth ?? 0,
        videoHeight: videoHeight ?? 0,
        videoFrameRate: videoFrameRate ?? 0,
        useNativePicker: useNativePicker ? "true" : "false",
    };
    if (errors.audio && !errors.audio.ok) {
        screenshareInfo["audioError"] = errors.audio.error.name;
    }
    if (errors.video && !errors.video.ok) {
        screenshareInfo["videoError"] = errors.video.error.name;
    }
    const electronDisplayInfo = await ElectronInterop?.getSystemDisplayInfo?.();
    if (electronDisplayInfo) {
        screenshareInfo["primaryDisplayId"] = electronDisplayInfo.primaryDisplayId ?? "none";
        screenshareInfo["totalDisplays"] = electronDisplayInfo.displays.length;
        electronDisplayInfo.displays.forEach((display, index) => {
            const prefix = `display${index}_`;
            screenshareInfo[`${prefix}id`] = display.id;
            screenshareInfo[`${prefix}width`] = display.size.width;
            screenshareInfo[`${prefix}height`] = display.size.height;
            screenshareInfo[`${prefix}scaleFactor`] = display.scaleFactor;
            screenshareInfo[`${prefix}internal`] = display.internal ? "true" : "false";
            if (videoWidth &&
                videoHeight &&
                display.size.width === videoWidth &&
                display.size.height === videoHeight) {
                screenshareInfo["videoMatchesSharingFullScreen"] = index;
            }
        });
    }
    metricsManager.addMetricReliably("screenshare-start", screenshareInfo);
}
export const getScreenshareStreamWithErrors = async (settings) => {
    let stream = undefined;
    let errors = {};
    try {
        stream = await getScreenshareStreamOrThrow(settings);
    }
    catch (e) {
        const error = Err(guaranteedGetUserMediaException(e));
        if (window.OverconstrainedError && error.error instanceof OverconstrainedError) {
            Logger.error("[LMM] Failed to setup screenshare stream", error.error, `overconstrained on ${error.error.constraint}`);
        }
        else {
            Logger.error("[LMM] Failed to setup screenshare stream", error.error);
        }
        if (isElectron && !settings.useNativePicker) {
            if (settings.sound) {
                errors = { audio: error };
                try {
                    stream = await getScreenshareStreamOrThrow({ ...settings, sound: false });
                }
                catch (errorOnRetry) {
                    Logger.error("[LMM] Failed to setup no-sound screenshare stream", errorOnRetry);
                    errors = { audio: error, video: error };
                }
            }
            else {
                errors = { video: error };
            }
        }
        else if (settings.useNativePicker) {
            if (error.error.name === "NotReadableError") {
                errors = {};
            }
            else {
                errors = { video: error };
            }
        }
        else {
            if (settings.sound) {
                errors = { audio: error, video: error };
            }
            else {
                errors = { video: error };
            }
        }
    }
    recordScreenshareResult({
        stream,
        errors,
        useNativePicker: settings.useNativePicker,
    });
    if (!stream)
        return errors;
    const videoTrack = stream.getVideoTracks()[0];
    const videoResult = videoTrack ? Ok(videoTrack) : errors.video ?? Err(new NoTrackError());
    if (!settings.sound)
        return { video: videoResult };
    const audioTrack = stream.getAudioTracks()[0];
    if (!isElectron && !audioTrack && !errors.audio)
        return { video: videoResult };
    const audioResult = audioTrack ? Ok(audioTrack) : errors.audio ?? Err(new NoTrackError());
    return { audio: audioResult, video: videoResult };
};
export const changeScreenshareTrackQuality = async (track, highQuality, resolution = ScreenshareResolution.HIGH, options) => {
    if (!track)
        return;
    const newConstraints = screenshareConstraints(highQuality, resolution, options);
    await track.applyConstraints(newConstraints);
    setContentHint(track, highQuality && resolution >= ScreenshareResolution.MED ? "detail" : "text");
};
//# sourceMappingURL=Screenshare.js.map